// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/QuestionFormAnswer.test.tsx
import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";
import { Text } from "@radix-ui/themes";
import cx from "classnames";

import {
  isFormAnswerContinuationLine,
  questionFormAnswerDisplayText,
} from "../../../utils/questionFormAnswerLine";
import {
  agentComponentIdAttr,
  type ChatAtomAgentComponentId,
  type ChatAtomAgentComponentInstanceId,
  CHAT_ATOM_AGENT_COMPONENT_IDS,
} from "../agentComponents/chatAtomRegistry";

export type QuestionFormPrompt = { header: string; question: string };

export type QuestionFormAnswerPair = { question: string; answer: string };

/**
 * `**Header:** answer` lines QuestionForm submits. A message that is not in
 * that shape is one answer with no header, so a card can still show it.
 */
export function parseQuestionFormAnswers(
  content: string,
): { header: string; answer: string }[] {
  const rawLines = content.split("\n").filter((line) => line.trim().length > 0);
  const pairs: { header: string; answer: string }[] = [];
  for (const rawLine of rawLines) {
    if (isFormAnswerContinuationLine(rawLine)) {
      const previous = pairs[pairs.length - 1];
      if (previous) {
        // why: stripping the continuation mark here makes a typed `Files:` line
        // look like the upload footer and stripTrailingUploadFooter drops it.
        previous.answer = `${previous.answer}\n${rawLine.trimEnd()}`;
      }
      continue;
    }
    const line = rawLine.trim();
    const match = /^\*\*(.+?):\*\*\s*(.*)$/.exec(line);
    if (!match) {
      // why: a textarea answer keeps its newline inside one form line, so the next line is more of the same answer
      const previous = pairs[pairs.length - 1];
      if (previous) previous.answer = `${previous.answer}\n${line}`;
      continue;
    }
    const answer = match[2]?.trim();
    if (answer) pairs.push({ header: match[1].trim(), answer });
  }
  if (pairs.length > 0) return pairs;
  const trimmed = content.trim();
  return trimmed ? [{ header: "", answer: trimmed }] : [];
}

/** Prompts the preceding assistant turn asked, atoms first, then legacy questions. */
export function questionFormPromptsFromMessage(
  message: Pick<AskPunttMessage, "components" | "userQuestions"> | undefined,
): QuestionFormPrompt[] {
  if (!message) return [];
  const fromAtoms: QuestionFormPrompt[] = [];
  for (const atom of message.components ?? []) {
    if (atom.componentId !== "ask-puntt.chat-atom.question-form") continue;
    for (const question of atom.payload.questions) {
      fromAtoms.push({
        header: question.header,
        question: question.question,
      });
    }
  }
  if (fromAtoms.length > 0) return fromAtoms;
  return (message.userQuestions ?? []).flatMap((question) =>
    question ? [{ header: question.header, question: question.question }] : [],
  );
}

/**
 * The question the reader was asked, paired with what they picked.
 * The header in the transcript is the short label; the prompt's `question`
 * is the sentence. A header with no prompt falls back to that label.
 */
/** True when `content` is a submit line for one of `prompts`, not a free-text reply. */
export function answersQuestionForm(
  content: string,
  prompts: readonly QuestionFormPrompt[],
): boolean {
  if (prompts.length === 0) return false;
  return parseQuestionFormAnswers(content).some(
    (pair) =>
      pair.header !== "" &&
      prompts.some((prompt) => prompt.header === pair.header),
  );
}

export function pairQuestionFormAnswers(
  content: string,
  prompts: readonly QuestionFormPrompt[] = [],
): QuestionFormAnswerPair[] {
  const unused = [...prompts];
  return parseQuestionFormAnswers(content).map((pair) => {
    const index = unused.findIndex((prompt) => prompt.header === pair.header);
    const prompt = index >= 0 ? unused.splice(index, 1)[0] : undefined;
    const question = prompt?.question || pair.header;
    return {
      question,
      answer: questionFormAnswerDisplayText(pair.answer, {
        storedQuestion: prompt?.question,
      }),
    };
  });
}

/**
 * A settled answer: the question in quiet type, the selection under it,
 * on a plain white card the width of the thread.
 * why-not: no Undo on this card yet — the completed layout is the question
 * and the choice, and the control comes back with that design.
 */
export function QuestionFormAnswer({
  question,
  answer,
  className,
  agentComponentId = CHAT_ATOM_AGENT_COMPONENT_IDS.questionFormAnswer,
  "data-testid": dataTestId = "question-form-answer",
}: {
  /** The question that was asked. Empty hides the top line. */
  question?: string;
  answer: string;
  className?: string;
  agentComponentId?:
    | ChatAtomAgentComponentId
    | ChatAtomAgentComponentInstanceId;
  "data-testid"?: string;
}) {
  const prompt = question?.trim() ?? "";
  const name = prompt ? `${prompt} ${answer}` : answer;

  return (
    <div
      {...agentComponentIdAttr(agentComponentId)}
      data-testid={dataTestId}
      aria-label={name}
      className={cx(
        "flex w-full flex-col items-start gap-2 rounded-xl border border-puntt-neutral-gray-6 bg-base-white px-4 py-3",
        className,
      )}
    >
      {prompt ? (
        <Text
          as="p"
          size="2"
          className="text-puntt-neutral-gray-11"
          data-testid={`${dataTestId}-question`}
        >
          {prompt}
        </Text>
      ) : null}
      <Text
        as="p"
        size="3"
        weight="medium"
        className="whitespace-pre-wrap text-puntt-neutral-gray-12"
        data-testid={`${dataTestId}-label`}
      >
        {answer}
      </Text>
    </div>
  );
}
